/**
 * ContentStudioPanel — the Content Engine's LANDING workflow.
 *
 * Four moves, top to bottom:
 *   1. TYPE PICKER  — LinkedIn post / X thread / blog / email / IG caption.
 *   2. TEMPLATES    — starter scaffolds per type; one click mints a Draft.
 *   3. KANBAN       — Idea → Draft → Scheduled → Published over the
 *                     `contentItems` store, with a date slot on Scheduled.
 *   4. ANALYTICS    — counts by status/type + honest average score.
 *
 * All pipeline semantics (grouping, stepping, summary) come from the PURE,
 * tested helpers in lib/content/pipeline. Scores come from the shipped
 * scorer in lib/crm/contentScore — reused, never reimplemented. The panel
 * only wires state to the store (lib/db/contentItems).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  LinkedinIcon,
  MessageSquareText,
  Newspaper,
  Mail,
  Camera,
  LayoutGrid,
  Sparkles,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CalendarClock,
  BarChart3,
  Gauge,
  Save,
  Lightbulb,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import type { ContentItem, ContentItemStatus, ContentItemType } from '../lib/db/types';
import {
  CONTENT_STATUSES,
  CONTENT_STATUS_LABELS,
  CONTENT_TYPES,
  advanceStatus,
  regressStatus,
  contentTypeMeta,
  platformForType,
  templatesForType,
  groupByStatus,
  summarizeContent,
  type ContentTemplate,
} from '../lib/content/pipeline';
import {
  bootstrapContentItemsIfEmpty,
  createContentItem,
  updateContentItem,
  deleteContentItem,
  subscribeContentItems,
} from '../lib/db/contentItems';
import { scoreDraft } from '../lib/crm/contentScore';
import { hasTool } from '../lib/content/studioHome';
import { ImageGenPanel } from './ImageGenPanel';

/** Icon per content type — kept here (UI concern), not in the pure module. */
const TYPE_ICONS: Record<ContentItemType, LucideIcon> = {
  'linkedin-post': LinkedinIcon,
  'x-thread': MessageSquareText,
  'x-post': MessageSquareText,
  blog: Newspaper,
  email: Mail,
  'ig-caption': Camera,
};

/** Column accent per stage. */
const STATUS_TINT: Record<ContentItemStatus, string> = {
  idea: C.textMuted,
  draft: C.blueElectric,
  scheduled: C.orange,
  published: C.green,
};

const scoreColor = (score: number) =>
  score >= 80 ? C.green : score >= 60 ? C.blueElectric : score >= 40 ? C.orange : C.orangeWarm;

/** Score a body with the shipped scorer; empty bodies stay honestly unscored. */
function scoreBody(body: string, type: ContentItemType): number | null {
  const trimmed = body.trim();
  if (!trimmed) return null;
  return scoreDraft(trimmed, { platform: platformForType(type) }).total;
}

// ---------------------------------------------------------------------------
// Kanban card
// ---------------------------------------------------------------------------

function KanbanCard({ item }: { item: ContentItem }) {
  const [open, setOpen] = useState(false);
  const [bodyDraft, setBodyDraft] = useState(item.body);
  const meta = contentTypeMeta(item.type);
  const Icon = TYPE_ICONS[item.type];
  const next = advanceStatus(item.status);
  const prev = regressStatus(item.status);
  const dirty = bodyDraft !== item.body;

  // Keep the local editor in sync when the record changes elsewhere.
  useEffect(() => setBodyDraft(item.body), [item.body]);

  const move = (status: ContentItemStatus) => {
    void updateContentItem(item.id, { status });
  };

  const saveBody = () => {
    void updateContentItem(item.id, {
      body: bodyDraft,
      score: scoreBody(bodyDraft, item.type),
    });
  };

  const setDate = (value: string) => {
    void updateContentItem(item.id, {
      scheduledFor: value ? new Date(`${value}T09:00:00.000Z`).toISOString() : null,
    });
  };

  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        padding: 10,
        background: 'rgba(255,255,255,0.03)',
        display: 'grid',
        gap: 8,
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          textAlign: 'left',
          width: '100%',
        }}
      >
        <Icon size={14} style={{ color: C.magentaSoft, flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-card-title" style={{ fontSize: 12, color: C.text }}>
            {item.title}
          </div>
          <div className="t-meta" style={{ margin: '2px 0 0', color: C.textDim }}>
            {item.id} · {meta.label}
          </div>
        </div>
        {item.score !== null && (
          <span
            className="glass-pill"
            title="Best-practice score (shipped scorer)"
            style={{
              fontSize: 10,
              padding: '2px 8px',
              flexShrink: 0,
              color: scoreColor(item.score),
              background: `${scoreColor(item.score)}22`,
              boxShadow: `inset 0 0 0 1px ${scoreColor(item.score)}55`,
              fontWeight: 600,
            }}
          >
            <Gauge size={9} style={{ marginRight: 3 }} />
            {item.score}
          </span>
        )}
      </button>

      {item.status === 'scheduled' && (
        <label
          className="t-meta"
          style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textMuted }}
        >
          <CalendarClock size={12} style={{ color: C.orange, flexShrink: 0 }} />
          <input
            type="date"
            value={item.scheduledFor ? item.scheduledFor.slice(0, 10) : ''}
            onChange={(e) => setDate(e.target.value)}
            aria-label={`Schedule date for ${item.id}`}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 6,
              color: C.text,
              fontSize: 11,
              padding: '3px 6px',
              colorScheme: 'dark',
            }}
          />
          {!item.scheduledFor && <span style={{ color: C.orangeWarm }}>pick a date</span>}
        </label>
      )}

      {open && (
        <div style={{ display: 'grid', gap: 6 }}>
          <textarea
            value={bodyDraft}
            onChange={(e) => setBodyDraft(e.target.value)}
            rows={6}
            placeholder="Draft body — scored on save with the best-practice scorer."
            aria-label={`Body of ${item.id}`}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              color: C.text,
              fontFamily: 'monospace',
              fontSize: 11,
              lineHeight: 1.5,
              padding: 8,
              resize: 'vertical',
            }}
          />
          {dirty && (
            <button
              onClick={saveBody}
              className="glass-btn glass-btn--primary"
              style={{ justifySelf: 'start', padding: '4px 10px', fontSize: 11 }}
            >
              <Save size={11} />
              Save + rescore
            </button>
          )}
          {hasTool(item.type, 'image-gen') && (
            <div
              style={{
                marginTop: 4,
                paddingTop: 10,
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <ImageGenPanel
                type={item.type}
                title={item.title}
                brief={bodyDraft || item.body}
              />
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => prev && move(prev)}
          disabled={!prev}
          className="glass-btn"
          title={prev ? `Move back to ${CONTENT_STATUS_LABELS[prev]}` : 'Already an idea'}
          style={{ padding: '3px 8px', fontSize: 11, opacity: prev ? 1 : 0.35 }}
        >
          <ChevronLeft size={11} />
          {prev ? CONTENT_STATUS_LABELS[prev] : '—'}
        </button>
        <button
          onClick={() => next && move(next)}
          disabled={!next}
          className="glass-btn"
          title={next ? `Advance to ${CONTENT_STATUS_LABELS[next]}` : 'Already published'}
          style={{ padding: '3px 8px', fontSize: 11, opacity: next ? 1 : 0.35 }}
        >
          {next ? CONTENT_STATUS_LABELS[next] : '—'}
          <ChevronRight size={11} />
        </button>
        <button
          onClick={() => void deleteContentItem(item.id)}
          className="glass-btn"
          title={`Delete ${item.id}`}
          style={{ padding: '3px 8px', marginLeft: 'auto' }}
        >
          <Trash2 size={11} style={{ color: C.orangeWarm }} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The studio panel
// ---------------------------------------------------------------------------

export function ContentStudioPanel({ lockedType }: { lockedType?: ContentItemType } = {}) {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [innerType, setInnerType] = useState<ContentItemType>('linkedin-post');
  const [ideaTitle, setIdeaTitle] = useState('');

  // When the workspace locks a type, the picker disappears and the board
  // shows ONLY that type — the decluttered per-type view.
  const selectedType = lockedType ?? innerType;
  const setSelectedType = setInnerType;

  useEffect(() => {
    bootstrapContentItemsIfEmpty().catch(() => undefined);
    return subscribeContentItems(setItems);
  }, []);

  const visibleItems = useMemo(
    () => (lockedType ? items.filter((i) => i.type === lockedType) : items),
    [items, lockedType],
  );
  const groups = useMemo(() => groupByStatus(visibleItems), [visibleItems]);
  const analytics = useMemo(() => summarizeContent(visibleItems), [visibleItems]);
  const templates = useMemo(() => templatesForType(selectedType), [selectedType]);
  const selectedMeta = contentTypeMeta(selectedType);

  const addIdea = async () => {
    const title = ideaTitle.trim();
    if (!title) return;
    setIdeaTitle('');
    await createContentItem({ title, type: selectedType, status: 'idea' });
  };

  const useTemplate = async (tpl: ContentTemplate) => {
    await createContentItem({
      title: `${tpl.name} — ${contentTypeMeta(tpl.type).label}`,
      type: tpl.type,
      status: 'draft',
      body: tpl.body,
      score: scoreBody(tpl.body, tpl.type),
      templateId: tpl.id,
    });
  };

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <LayoutGrid size={16} style={{ color: C.magenta, flexShrink: 0 }} />
        <span className="t-section-title" style={{ color: C.text }}>
          Content studio
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          {lockedType
            ? `${selectedMeta.label} only · start from a template · move it Idea → Published`
            : 'pick a type · start from a template · move it Idea → Published'}
        </span>
      </div>

      {/* 1 — Type picker (hidden when the workspace already picked a type) */}
      {!lockedType && (
      <div style={{ display: 'grid', gap: 8 }}>
        <div className="t-eyebrow">1 · What are you making?</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 8,
          }}
        >
          {CONTENT_TYPES.map((t) => {
            const Icon = TYPE_ICONS[t.id];
            const active = selectedType === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedType(t.id)}
                aria-pressed={active}
                style={{
                  background: active ? 'rgba(255,0,255,0.1)' : 'rgba(255,255,255,0.03)',
                  border: active
                    ? '1px solid rgba(255,0,255,0.35)'
                    : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  padding: 10,
                  cursor: 'pointer',
                  display: 'grid',
                  gap: 4,
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon size={14} style={{ color: active ? C.magenta : C.textMuted }} />
                  <span className="t-card-title" style={{ fontSize: 12, color: active ? '#fff' : C.text }}>
                    {t.label}
                  </span>
                </div>
                <span className="t-meta" style={{ color: C.textDim }}>
                  {t.description}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* 2 — Templates gallery + quick idea capture */}
      <div style={{ display: 'grid', gap: 8 }}>
        <div className="t-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Sparkles size={12} style={{ color: C.magentaSoft }} />
          {lockedType ? '1' : '2'} · Start from a {selectedMeta.label} template
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: 8,
          }}
        >
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: 10,
                background: 'rgba(255,255,255,0.03)',
                display: 'grid',
                gap: 6,
                alignContent: 'start',
              }}
            >
              <div className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                {tpl.name}
              </div>
              <div className="t-meta" style={{ color: C.textDim }}>
                {tpl.description}
              </div>
              <pre
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 10,
                  lineHeight: 1.4,
                  color: C.textMuted,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  padding: 8,
                  maxHeight: 90,
                  overflow: 'hidden',
                }}
              >
                {tpl.body}
              </pre>
              <button
                onClick={() => void useTemplate(tpl)}
                className="glass-btn glass-btn--primary"
                style={{ justifySelf: 'start', padding: '4px 10px', fontSize: 11 }}
              >
                <PlusCircle size={11} />
                Use template → Draft
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Lightbulb size={13} style={{ color: C.orange, flexShrink: 0 }} />
          <input
            value={ideaTitle}
            onChange={(e) => setIdeaTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void addIdea()}
            placeholder={`Or capture a bare ${selectedMeta.label} idea…`}
            aria-label="New idea title"
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              color: C.text,
              fontSize: 12,
              padding: '7px 10px',
            }}
          />
          <button
            onClick={() => void addIdea()}
            disabled={!ideaTitle.trim()}
            className="glass-btn"
            style={{ padding: '6px 12px', fontSize: 11, opacity: ideaTitle.trim() ? 1 : 0.4 }}
          >
            <PlusCircle size={12} />
            Add idea
          </button>
        </div>
      </div>

      {/* 3 — Kanban */}
      <div style={{ display: 'grid', gap: 8 }}>
        <div className="t-eyebrow">{lockedType ? '2' : '3'} · Pipeline</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 10,
            alignItems: 'start',
          }}
        >
          {CONTENT_STATUSES.map((status) => {
            const tint = STATUS_TINT[status];
            const column = groups[status];
            return (
              <div
                key={status}
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 12,
                  padding: 10,
                  background: 'rgba(255,255,255,0.02)',
                  display: 'grid',
                  gap: 8,
                  alignContent: 'start',
                  minHeight: 80,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: tint,
                      flexShrink: 0,
                    }}
                  />
                  <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                    {CONTENT_STATUS_LABELS[status]}
                  </span>
                  <span className="t-meta" style={{ marginLeft: 'auto', color: C.textDim }}>
                    {column.length}
                  </span>
                </div>
                {column.length === 0 ? (
                  <div className="t-meta" style={{ color: C.textDim }}>
                    Nothing here yet.
                  </div>
                ) : (
                  column.map((item) => <KanbanCard key={item.id} item={item} />)
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 4 — Analytics */}
      <div style={{ display: 'grid', gap: 8 }}>
        <div className="t-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChart3 size={12} style={{ color: C.blueElectric }} />
          {lockedType ? `3 · ${selectedMeta.label} analytics` : '4 · Pipeline analytics'}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className="glass-pill" style={{ fontSize: 11, padding: '4px 10px' }}>
            {analytics.total} item{analytics.total === 1 ? '' : 's'}
          </span>
          {CONTENT_STATUSES.map((s) => (
            <span
              key={s}
              className="glass-pill"
              style={{
                fontSize: 11,
                padding: '4px 10px',
                color: STATUS_TINT[s],
                background: `${STATUS_TINT[s]}18`,
                boxShadow: `inset 0 0 0 1px ${STATUS_TINT[s]}44`,
              }}
            >
              {CONTENT_STATUS_LABELS[s]} · {analytics.byStatus[s]}
            </span>
          ))}
          <span
            className="glass-pill"
            title={
              analytics.avgScore === null
                ? 'No scored drafts yet — scores appear once a body is saved.'
                : `Average of ${analytics.scoredCount} scored draft${analytics.scoredCount === 1 ? '' : 's'}`
            }
            style={{
              fontSize: 11,
              padding: '4px 10px',
              color: analytics.avgScore === null ? C.textDim : scoreColor(analytics.avgScore),
              fontWeight: 600,
            }}
          >
            <Gauge size={10} style={{ marginRight: 4 }} />
            avg score:{' '}
            {analytics.avgScore === null ? '— (unscored)' : `${analytics.avgScore}/100 (n=${analytics.scoredCount})`}
          </span>
          {analytics.upcomingScheduled > 0 && (
            <span
              className="glass-pill"
              style={{ fontSize: 11, padding: '4px 10px', color: C.orange }}
            >
              <CalendarClock size={10} style={{ marginRight: 4 }} />
              {analytics.upcomingScheduled} upcoming
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CONTENT_TYPES.filter((t) => analytics.byType[t.id] > 0).map((t) => (
            <span key={t.id} className="t-meta" style={{ color: C.textMuted }}>
              {t.label}: {analytics.byType[t.id]}
            </span>
          ))}
        </div>
      </div>
    </GlassPanel>
  );
}
